import {
  useEffect,
  useState,
} from "react";

import {
  FaHistory,
  FaSyncAlt,
} from "react-icons/fa";

import "./StoricoViaggiFlotta.css";

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "https://interventi.synology.me:5050";

const formatDateTime = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return (
    <span className="flotta-history-datetime">
      <span>{date.toLocaleDateString("it-IT")}</span>
      <span>
        {date.toLocaleTimeString("it-IT", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })}
      </span>
    </span>
  );
};


const formatDepartureDateTime = (partenza, assegnato) => {
  if (partenza) {
    return formatDateTime(partenza);
  }

  if (!assegnato) {
    return "—";
  }

  const date = new Date(assegnato);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return (
    <>
      {date.toLocaleDateString("it-IT")}
      <br />
      <span>orario n/d</span>
    </>
  );
};

const formatDuration = (seconds) => {
  const value = Number(seconds);

  if (!Number.isFinite(value)) {
    return "—";
  }

  const totalMinutes = Math.max(
    0,
    Math.round(value / 60)
  );

  const hours = Math.floor(
    totalMinutes / 60
  );

  const minutes =
    totalMinutes % 60;

  if (hours > 0) {
    return `${hours} h ${minutes} min`;
  }

  return `${minutes} min`;
};

const formatDistance = (meters) => {
  const value = Number(meters);

  if (!Number.isFinite(value)) {
    return "—";
  }

  return `${(value / 1000).toFixed(2)} km`;
};

const getStatusLabel = (status) => {
  const labels = {
    assegnato: "Assegnato",
    in_viaggio: "In viaggio",
    arrivato: "Arrivato",
    completato: "Completato",
    annullato: "Annullato",
  };

  return labels[status] || status || "—";
};

const StoricoViaggiFlotta = ({
  refreshKey = 0,
  onApriIntervento,
}) => {
  const [trips, setTrips] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const loadTrips = async () => {
    try {
      const token =
        localStorage.getItem("token");

      const response = await fetch(
        `${API_BASE_URL}/api/flotta/viaggi?limit=10`,
        {
          headers: token
            ? {
                Authorization:
                  `Bearer ${token}`,
              }
            : {},
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.error ||
            `HTTP ${response.status}`
        );
      }

      const storicoTrips =
        Array.isArray(data.trips)
          ? data.trips.filter(
              (trip) =>
                trip.stato === "completato" ||
                trip.stato === "annullato"
            )
          : [];

      setTrips(storicoTrips);

      setError("");
    } catch (requestError) {
      console.error(
        "Errore storico viaggi:",
        requestError
      );

      setError(
        "Impossibile caricare lo storico viaggi"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleApriIntervento = async (trip) => {
    const interventoId = Number(trip?.intervento_id);

    if (!Number.isInteger(interventoId) || interventoId <= 0) {
      window.alert("Intervento non valido");
      return;
    }

    try {
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${API_BASE_URL}/interventi/${interventoId}`,
        {
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : {},
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const intervento = await response.json();

      onApriIntervento?.(intervento);
    } catch (requestError) {
      console.error(
        "Errore apertura intervento da Flotta:",
        requestError
      );

      window.alert(
        "Impossibile aprire l'intervento completo"
      );
    }
  };

  useEffect(() => {
    loadTrips();

    const interval = setInterval(
      loadTrips,
      30000
    );

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (refreshKey > 0) {
      loadTrips();
    }
  }, [refreshKey]);

  return (
    <section className="flotta-card flotta-history-card">
      <div className="flotta-history-header">
        <div>
          <h3>
            <FaHistory />
            Storico viaggi Fiorino
          </h3>

          <p>
            Ultimi 10 viaggi rilevati
          </p>
        </div>

        <button
          type="button"
          onClick={loadTrips}
          title="Aggiorna storico viaggi"
        >
          <FaSyncAlt />
          Aggiorna
        </button>
      </div>

      {loading && trips.length === 0 ? (
        <p>Caricamento storico...</p>
      ) : error && trips.length === 0 ? (
        <p className="flotta-history-error">
          {error}
        </p>
      ) : trips.length === 0 ? (
        <p className="flotta-history-empty">
          Nessun viaggio registrato.
        </p>
      ) : (
        <div className="flotta-history-table-wrapper">
          <table className="flotta-history-table">
            <thead>
              <tr>
                <th>Intervento</th>
                <th>Stato</th>
                <th>Destinazione</th>
                <th>Partenza</th>
                <th>Arrivo</th>
                <th>Durata</th>
                <th>Distanza GPS</th>
                <th>Azioni</th>
              </tr>
            </thead>

            <tbody>
              {trips.map((trip) => (
                <tr key={trip.id}>
                  <td>
                    {trip.numero_foglio_intervento || `#${trip.intervento_id}`}
                  </td>

                  <td>
                    <span
                      className={
                        `flotta-history-status ${trip.stato}`
                      }
                    >
                      {getStatusLabel(
                        trip.stato
                      )}
                    </span>
                  </td>

                  <td>
                    {trip.indirizzo_destinazione ||
                      "—"}
                  </td>

                  <td>
                    {formatDepartureDateTime(
                  trip.partenza_il,
                  trip.assegnato_il
                )}
                  </td>

                  <td>
                    {formatDateTime(
                      trip.arrivo_il
                    )}
                  </td>

                  <td>
                    {formatDuration(
                      trip.durata_viaggio_sec
                    )}
                  </td>

                  <td>
                    {formatDistance(
                      trip.distanza_gps_m
                    )}
                  </td>

                  <td>
                    {Number(trip.intervento_esistente) === 0 ? (
                      <span className="flotta-history-removed">
                        Intervento rimosso
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="flotta-history-open-button"
                        onClick={() => handleApriIntervento(trip)}
                      >
                        Apri intervento
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {error && trips.length > 0 && (
        <p className="flotta-history-error">
          {error}. Sono mostrati gli ultimi dati disponibili.
        </p>
      )}
    </section>
  );
};

export default StoricoViaggiFlotta;
